import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { NOTIFICATION_TYPE_LABELS } from "@/constants/notifications";
import { notificationService } from "@/lib/services";
import type { Notification, NotificationStreamEventMap } from "@/types";
import { useAuth } from "./AuthContext";

type NotificationEventHandler = (data: unknown) => void;

interface NotificationContextType {
  /** Recent notifications (first page) */
  notifications: Notification[];
  /** Total unread count */
  unreadCount: number;
  /** Whether initial data is loading */
  isLoading: boolean;
  /** Browser notification permission state */
  desktopPermission: NotificationPermission;

  /** Refresh notification list + count from server */
  refresh: () => Promise<void>;
  /** Mark a single notification as read */
  markRead: (uuid: string) => Promise<void>;
  /** Mark all notifications as read */
  markAllRead: () => Promise<void>;
  /** Delete a single notification */
  deleteNotification: (uuid: string) => Promise<void>;
  /** Request browser notification permission */
  requestDesktopPermission: () => Promise<NotificationPermission>;
  /** Subscribe to an event from the notification stream. Returns an unsubscribe function. */
  subscribe: (event: keyof NotificationStreamEventMap, handler: NotificationEventHandler) => () => void;
}

const NotificationContext = createContext<NotificationContextType | null>(null);

export function NotificationProvider({ children }: Readonly<{ children: ReactNode }>) {
  const { isAuthenticated } = useAuth();

  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [desktopPermission, setDesktopPermission] = useState<NotificationPermission>(
    typeof globalThis !== "undefined" && "Notification" in globalThis ? globalThis.Notification.permission : "default",
  );

  // SSE abort handle
  const sseRef = useRef<{ abort: () => void } | null>(null);
  // Reconnect timer
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Ref for isAuthenticated to avoid stale closures in reconnect
  const isAuthenticatedRef = useRef(isAuthenticated);
  isAuthenticatedRef.current = isAuthenticated;
  // Stream event subscribers, keyed by SSE event name
  const handlersRef = useRef(new Map<string, Set<NotificationEventHandler>>());

  // ── Fetch helpers ──────────────────────────────────────────────

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await notificationService.list({ page: 1, pageSize: 20 });
      setNotifications(res.data);
    } catch (err) {
      console.error("Failed to fetch notifications:", err);
    }
  }, []);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await notificationService.getUnreadCount();
      setUnreadCount(res.data.count);
    } catch (err) {
      console.error("Failed to fetch unread count:", err);
    }
  }, []);

  const refresh = useCallback(async () => {
    await Promise.all([fetchNotifications(), fetchUnreadCount()]);
  }, [fetchNotifications, fetchUnreadCount]);

  // ── Desktop notification helper ────────────────────────────────

  const showDesktopNotification = useCallback((notification: Notification, shouldShow: boolean) => {
    if (!shouldShow) return;

    if (
      typeof globalThis === "undefined" ||
      !("Notification" in globalThis) ||
      globalThis.Notification.permission !== "granted"
    ) {
      return;
    }

    const typeLabel = NOTIFICATION_TYPE_LABELS[notification.type] || notification.type;

    const n = new globalThis.Notification(notification.title || typeLabel, {
      body: notification.message,
      icon: "/favicon.svg",
      tag: notification.uuid,
    });

    n.onclick = () => {
      window.focus();
      n.close();
    };
  }, []);

  // ── SSE connection ─────────────────────────────────────────────

  // Store showDesktopNotification in a ref so the SSE handler stays stable
  const showDesktopRef = useRef(showDesktopNotification);
  showDesktopRef.current = showDesktopNotification;

  // ── Lifecycle ──────────────────────────────────────────────────

  useEffect(() => {
    if (!isAuthenticated) {
      // Clear state on logout
      setNotifications([]);
      setUnreadCount(0);
      sseRef.current?.abort();
      sseRef.current = null;
      return;
    }

    // Load initial data
    setIsLoading(true);
    Promise.all([fetchNotifications(), fetchUnreadCount()]).finally(() => setIsLoading(false));

    // Open SSE connection with exponential backoff on failure
    let retryAttempt = 0;
    const MAX_RETRY_DELAY_MS = 60_000; // Cap at 1 minute between retries
    const BASE_RETRY_DELAY_MS = 2_000; // Start at 2 seconds

    const connectSSE = () => {
      sseRef.current?.abort();
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }

      const handle = notificationService.stream(
        (event, data) => {
          // Successful event received — reset retry counter
          retryAttempt = 0;

          if (event === "notification") {
            const payload = data as NotificationStreamEventMap["notification"];
            const notification: Notification = {
              uuid: payload.uuid,
              type: payload.type,
              title: payload.title,
              message: payload.message,
              metadata: payload.metadata,
              readAt: payload.readAt,
              createdAt: payload.createdAt,
            };
            setNotifications((prev) => [notification, ...prev].slice(0, 20)); // NOSONAR — arrow callback in state updater; prepend-and-cap logic
            setUnreadCount((prev) => prev + 1); // NOSONAR — arrow callback in state updater; simple increment
            showDesktopRef.current(notification, !!payload.showDesktop);
          } else if (event === "unread_count") {
            const { count } = data as NotificationStreamEventMap["unread_count"];
            setUnreadCount(count);
          }
          for (const handler of handlersRef.current.get(event) ?? []) {
            try {
              handler(data);
            } catch (err) {
              console.error(`Notification stream "${event}" handler failed:`, err);
            }
          }
        },
        () => {
          // On disconnect, attempt to reconnect with exponential backoff
          retryAttempt++;
          const delay = Math.min(BASE_RETRY_DELAY_MS * 2 ** (retryAttempt - 1), MAX_RETRY_DELAY_MS);
          reconnectTimerRef.current = setTimeout(() => {
            if (isAuthenticatedRef.current) {
              connectSSE();
            }
          }, delay);
        },
      );

      sseRef.current = handle;
    };

    connectSSE();

    return () => {
      sseRef.current?.abort();
      sseRef.current = null;
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
    };
    // Only reconnect SSE when authentication state changes
  }, [isAuthenticated]);

  // ── Actions ────────────────────────────────────────────────────

  // Ref for notifications to allow stable markRead callback
  const notificationsRef = useRef(notifications);
  notificationsRef.current = notifications;

  const markRead = useCallback(async (uuid: string) => {
    // Guard: skip if notification is already read
    const existing = notificationsRef.current.find((n) => n.uuid === uuid);
    if (existing?.readAt) return;

    try {
      await notificationService.markRead(uuid);
      setNotifications((prev) => prev.map((n) => (n.uuid === uuid ? { ...n, readAt: new Date().toISOString() } : n)));
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (err) {
      console.error("Failed to mark notification as read:", err);
    }
  }, []);

  const markAllRead = useCallback(async () => {
    try {
      await notificationService.markAllRead();
      setNotifications((prev) =>
        prev.map((n) => ({
          ...n,
          readAt: n.readAt || new Date().toISOString(),
        })),
      );
      setUnreadCount(0);
    } catch (err) {
      console.error("Failed to mark all notifications as read:", err);
    }
  }, []);

  const deleteNotification = useCallback(
    async (uuid: string) => {
      try {
        await notificationService.delete(uuid);
        setNotifications((prev) => prev.filter((n) => n.uuid !== uuid));
        // Refresh unread count since we don't know if deleted was unread
        fetchUnreadCount();
      } catch (err) {
        console.error("Failed to delete notification:", err);
      }
    },
    [fetchUnreadCount],
  );

  const requestDesktopPermission = useCallback(async (): Promise<NotificationPermission> => {
    if (typeof globalThis === "undefined" || !("Notification" in globalThis)) {
      return "denied";
    }
    const permission = await globalThis.Notification.requestPermission();
    setDesktopPermission(permission);
    return permission;
  }, []);

  const subscribe = useCallback((event: keyof NotificationStreamEventMap, handler: NotificationEventHandler) => {
    const handlers = handlersRef.current.get(event) ?? new Set();
    handlers.add(handler);
    handlersRef.current.set(event, handlers);
    return () => {
      handlers.delete(handler);
    };
  }, []);

  const value = useMemo(
    () => ({
      notifications,
      unreadCount,
      isLoading,
      desktopPermission,
      refresh,
      markRead,
      markAllRead,
      deleteNotification,
      requestDesktopPermission,
      subscribe,
    }),
    [
      notifications,
      unreadCount,
      isLoading,
      desktopPermission,
      refresh,
      markRead,
      markAllRead,
      deleteNotification,
      requestDesktopPermission,
      subscribe,
    ],
  );

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
}

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error("useNotifications must be used within a NotificationProvider");
  }
  return context;
}

export function useNotificationEvent<K extends keyof NotificationStreamEventMap>(
  event: K,
  handler: (data: NotificationStreamEventMap[K]) => void,
) {
  const { subscribe } = useNotifications();
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(
    () => subscribe(event, (data) => handlerRef.current(data as NotificationStreamEventMap[K])),
    [subscribe, event],
  );
}
